// frontend/src/components/playground/CharacterPreferenceModal.jsx
/**
 * Character Preference Modal - Let users choose their guide's gender
 */

import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XMarkIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import PlaygroundCharacter from './PlaygroundCharacter';

const CharacterPreferenceModal = ({ isOpen, onClose, onSelect, currentPreference = 'neutral' }) => {
  const [selectedGender, setSelectedGender] = useState(currentPreference);

  const characterOptions = [
    { 
      id: 'male', 
      label: 'Alex', 
      subtitle: 'Male Guide',
      description: 'A friendly and knowledgeable male financial mentor who will guide you through your investment journey',
      gender: 'male'
    },
    { 
      id: 'female', 
      label: 'Sam', 
      subtitle: 'Female Guide',
      description: 'A friendly and knowledgeable female financial mentor who will guide you through your investment journey',
      gender: 'female'
    },
    { 
      id: 'neutral', 
      label: 'Aura', 
      subtitle: 'Neutral Guide',
      description: 'A friendly and knowledgeable gender-neutral guide who will help you learn and grow',
      gender: 'neutral'
    }
  ];

  const handleSelect = (option) => {
    setSelectedGender(option.id);
  };

  const handleConfirm = () => {
    localStorage.setItem('playground_character_preference', selectedGender);
    if (onSelect) {
      onSelect(selectedGender);
    }
    onClose();
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0, y: 20 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.9, opacity: 0, y: 20 }}
          className="bg-glass-card backdrop-blur-xl rounded-2xl border border-aurora-purple/30 shadow-2xl max-w-md w-full p-6"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-2xl font-bold text-white">Choose Your Guide</h2>
              <p className="text-sm text-gray-400 mt-1">Select a character to guide you through the Playground</p>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-white hover:bg-white/10 rounded-lg transition"
            >
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>

          {/* Character Options */}
          <div className="space-y-3 mb-6">
            {characterOptions.map((option) => (
              <motion.button
                key={option.id}
                onClick={() => handleSelect(option)}
                className={`w-full p-4 rounded-xl border-2 transition-all ${
                  selectedGender === option.id
                    ? 'border-aurora-purple bg-aurora-purple/20'
                    : 'border-gray-700 bg-gray-800/50 hover:border-aurora-purple/50'
                }`}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <div className="flex items-center gap-4">
                  <div className="flex-shrink-0">
                    <PlaygroundCharacter 
                      gender={option.gender} 
                      size="lg"
                      expression="happy"
                      animated={false}
                    />
                  </div>
                  <div className="flex-1 text-left">
                    <div className="flex items-center gap-2">
                      <div>
                        <h3 className="text-lg font-semibold text-white">{option.label}</h3>
                        <p className="text-xs text-gray-500">{option.subtitle}</p>
                      </div>
                      {selectedGender === option.id && (
                        <CheckCircleIcon className="w-5 h-5 text-aurora-purple ml-auto" />
                      )}
                    </div>
                    <p className="text-sm text-gray-400 mt-1">{option.description}</p>
                  </div>
                </div>
              </motion.button>
            ))}
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="flex-1 py-3 px-4 bg-gray-700 hover:bg-gray-600 rounded-lg text-white font-medium transition"
            >
              Cancel
            </button>
            <button
              onClick={handleConfirm}
              className="flex-1 py-3 px-4 bg-gradient-to-r from-aurora-purple to-aurora-blue rounded-lg text-white font-semibold hover:opacity-90 transition"
            >
              Confirm Selection
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default CharacterPreferenceModal;
